import { useState } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import { useOrderNotes, useCreateOrderNote } from '../../hooks/useOrders';
import { formatDate } from '../../utils/date';

interface OrderNotesProps {
  orderUrn: string;
}

export default function OrderNotes({ orderUrn }: OrderNotesProps) {
  const { data: rawNotes, isLoading } = useOrderNotes(orderUrn);
  const notes = Array.isArray(rawNotes) ? rawNotes : [];
  const createNote = useCreateOrderNote(orderUrn);
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    createNote.mutate(
      { message: message.trim(), type: 'admin_note' },
      { onSuccess: () => setMessage('') },
    );
  };

  return (
    <div>
      {/* Note input */}
      <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
        <input
          type="text"
          className="input flex-1"
          placeholder="Add a note..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <button
          type="submit"
          disabled={!message.trim() || createNote.isPending}
          className="btn-primary gap-1"
        >
          <Send className="h-4 w-4" />
          Send
        </button>
      </form>

      {/* Notes list */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
        </div>
      ) : !notes || notes.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No notes yet.</p>
      ) : (
        <div className="space-y-3">
          {[...notes].reverse().map((note, idx) => (
            <div key={idx} className="flex gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
              <MessageSquare className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-900 dark:text-gray-100">{note.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {formatDate(note.date)}
                  </span>
                  {note.type && (
                    <span className="badge bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300">{note.type}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
